import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

const DEFAULT_CALENDLY_URL =
  "https://calendly.com/tchatixdemo/audit?background_color=001d31&text_color=ffffff&primary_color=0985d8";

/**
 * Modale Calendly partagée.
 *
 * Rendue via un portail vers document.body : elle échappe à tout ancêtre
 * transformé (animations d'entrée du header, etc.) qui cassait le
 * `position: fixed` et empêchait de fermer la popup.
 *
 * Fermeture : clic sur le fond, bouton X, touche Échap. Le scroll de fond
 * est verrouillé tant que la modale est ouverte.
 */
export default function CalendlyModal({ open, onClose, src = DEFAULT_CALENDLY_URL }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="tx-modal-fade fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 backdrop-blur-sm md:p-4"
      onClick={onClose}
    >
      <div
        className="tx-modal-panel relative flex flex-col w-full h-full bg-white overflow-hidden md:max-w-3xl md:h-[680px] md:max-h-[90vh] md:rounded-3xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Barre supérieure sticky — toujours visible, gros bouton X tappable */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#001D31]/10 bg-white shrink-0">
          <p className="text-sm font-semibold text-[#001D31] truncate">{t("nav.demoTitle")}</p>
          <button
            onClick={onClose}
            className="shrink-0 w-11 h-11 rounded-full bg-[#001D31]/5 hover:bg-[#001D31]/10 flex items-center justify-center text-[#001D31] transition-colors"
            aria-label={t("nav.closeDemo")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <iframe
          src={src}
          title={t("nav.demoTitle")}
          className="w-full block flex-1 min-h-0"
          style={{ border: "0" }}
        />
      </div>
    </div>,
    document.body
  );
}